import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const VariantEditor = ({ value = [], onChange }) => {
  const update = (i, patch) => onChange(value.map((g, j) => (j === i ? { ...g, ...patch } : g)));
  const updateOpt = (i, k, patch) =>
    update(i, { options: value[i].options.map((o, j) => (j === k ? { ...o, ...patch } : o)) });

  return (
    <div className="space-y-3" data-testid="variant-editor">
      {value.map((g, i) => (
        <div key={i} className="rounded-md border border-border p-3" data-testid={`variant-group-${i}`}>
          <div className="flex items-center gap-2">
            <Input
              data-testid={`variant-group-name-${i}`}
              value={g.name}
              placeholder="Option group (e.g. Color, HP, Size)"
              onChange={(e) => update(i, { name: e.target.value })}
              className="h-9"
            />
            <Input
              data-testid={`variant-group-fit-${i}`}
              value={g.fit_widths || ""}
              placeholder='Fits widths (e.g. 50-54)'
              title="Only show this whole group for these hull widths (inches). Leave blank for all."
              onChange={(e) => update(i, { fit_widths: e.target.value })}
              className="h-9 w-40 flex-shrink-0 text-xs"
            />
            <label className="flex flex-shrink-0 items-center gap-1 whitespace-nowrap text-xs" title="Let the customer tick more than one choice in this group (prices add up)">
              <input
                type="checkbox"
                data-testid={`variant-group-multi-${i}`}
                checked={!!g.multi}
                onChange={(e) => update(i, { multi: e.target.checked })}
                className="h-3.5 w-3.5 accent-[#1B66C9]"
              />
              pick many
            </label>
            <button
              type="button"
              data-testid={`variant-group-remove-${i}`}
              onClick={() => onChange(value.filter((_, j) => j !== i))}
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md text-[#DC2626] hover:bg-secondary"
            >
              <Trash2 size={15} />
            </button>
          </div>
          <div className="mt-2 space-y-1.5">
            {g.options.map((o, k) => (
              <div key={k} className="flex items-center gap-2">
                <Input
                  data-testid={`variant-opt-label-${i}-${k}`}
                  value={o.label}
                  placeholder="Choice (e.g. Camo)"
                  onChange={(e) => updateOpt(i, k, { label: e.target.value })}
                  className="h-8 text-sm"
                />
                <div className="flex items-center gap-1">
                  <span className="text-xs text-muted-foreground">+$</span>
                  <Input
                    type="number"
                    data-testid={`variant-opt-delta-${i}-${k}`}
                    value={o.price_delta}
                    onChange={(e) => updateOpt(i, k, { price_delta: e.target.value })}
                    className="h-8 w-24 text-sm"
                  />
                </div>
                <Input
                  data-testid={`variant-opt-fit-${i}-${k}`}
                  value={o.fit_widths || ""}
                  placeholder="fits 44,50"
                  title="Hull widths this choice fits (inches). Blank = all."
                  onChange={(e) => updateOpt(i, k, { fit_widths: e.target.value })}
                  className="h-8 w-28 flex-shrink-0 text-xs"
                />
                <label className="flex flex-shrink-0 items-center gap-1 whitespace-nowrap text-xs" title="Customer can enter how many (price × quantity). Only applies in 'pick many' groups.">
                  <input
                    type="checkbox"
                    data-testid={`variant-opt-perunit-${i}-${k}`}
                    checked={!!o.per_unit}
                    onChange={(e) => updateOpt(i, k, { per_unit: e.target.checked })}
                    className="h-3.5 w-3.5 accent-[#1B66C9]"
                  />
                  qty
                </label>
                <button
                  type="button"
                  onClick={() => update(i, { options: g.options.filter((_, j) => j !== k) })}
                  className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid={`variant-add-option-${i}`}
              onClick={() => update(i, { options: [...g.options, { label: "", price_delta: 0, fit_widths: "" }] })}
              className="h-8 text-xs font-bold"
            >
              <Plus size={13} className="mr-1" /> Add choice
            </Button>
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid="variant-add-group"
        onClick={() => onChange([...value, { name: "", fit_widths: "", options: [{ label: "", price_delta: 0, fit_widths: "" }] }])}
        className="text-xs font-bold"
      >
        <Plus size={13} className="mr-1" /> Add option group
      </Button>
    </div>
  );
};
